import { Box, Typography } from "@mui/material"
import { CheckOutlined, CloseOutlined, SyncOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement } from "react"
import React, { useMemo } from "react"

import { pluralize } from "../../../utils/stringUtils"
import { PricingDialogs } from "../constants"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import { type PriceChangeSubmission, PriceChangeSubmissionSource } from "@/graphql/codegen/graphql"
import { useDenyPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useDenyPriceChangeSubmission"
import { useRevertPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useRevertPriceChangeSubmission"
import { calculateTotalPricingVariations } from "@/screens/Pricing/utils"

interface ReviewSubmissionHeaderProps {
  priceChangeSubmission: PriceChangeSubmission
  onDeny?: () => void
  onApprove?: () => void
  onSaveAndExit?: () => void
  onRevert: (submissionId: string) => void
  setIsDialogOpen: (dialog: PricingDialogs | null) => void
  isResolved?: boolean
  isSellingPrice: boolean
  isDialogOpen: PricingDialogs | null
  hasAnyChanges: boolean
  hasMaintenancePermission: boolean
  selectedCounts: Record<string, number>
  total: number
  modifiedTiersCount: number
  totalInvalidCount?: number
  isFetching?: boolean
}

const ReviewSubmissionHeader: FC<ReviewSubmissionHeaderProps> = ({
  onApprove,
  onRevert,
  onDeny,
  onSaveAndExit,
  isDialogOpen,
  setIsDialogOpen,
  isResolved,
  isSellingPrice,
  hasMaintenancePermission,
  priceChangeSubmission,
  hasAnyChanges,
  selectedCounts,
  total,
  modifiedTiersCount,
  totalInvalidCount = 0,
  isFetching = false,
}): ReactElement => {
  const { denyPriceChangeSubmission } = useDenyPriceChangeSubmission()
  const { revertPriceChangeSubmission } = useRevertPriceChangeSubmission()

  const priceChangeItemsCount = useMemo(
    () => priceChangeSubmission.priceChangeItems?.length ?? 0,
    [priceChangeSubmission]
  )

  const totalPricingVariations = calculateTotalPricingVariations(priceChangeSubmission?.priceChangeItems)
  const totalSelected = (priceChangeSubmission.priceChangeItems ?? []).reduce(
    (acc, pcItems) => acc + (selectedCounts[pcItems.id] ?? 0),
    0
  )

  const isQuoteSource = priceChangeSubmission.source === PriceChangeSubmissionSource.SalesPipelineQuoteRequest
  const isSecondaryTextDisplay = !isResolved && totalPricingVariations > 0
  const denyLabel = isQuoteSource ? "Deny Quote" : "Deny Price Change"
  const approveLabel = isQuoteSource ? "Approve Quote" : "Approve Price Change"
  const isButtonDisabled = isFetching || !hasMaintenancePermission
  const hasInvalidTiers = totalInvalidCount > 0

  const handleDeny = async () => {
    if (!priceChangeSubmission?.id) {
      return
    }

    const isSuccess = await denyPriceChangeSubmission({
      submissionId: priceChangeSubmission.id,
    })
    if (!isSuccess) {
      return
    }

    setIsDialogOpen(null)
    onDeny?.()
  }

  const handleRevert = async () => {
    if (!priceChangeSubmission?.id) {
      return
    }

    const revertedSubmissionId = await revertPriceChangeSubmission({ submissionId: priceChangeSubmission.id })

    if (revertedSubmissionId) {
      onRevert(revertedSubmissionId)
    }
  }

  return (
    <Box className='flex w-full flex-row justify-between'>
      <Box className='flex flex-col gap-1'>
        {!isFetching && (
          <Typography variant='body1'>
            Showing {priceChangeItemsCount} of {total} {pluralize(total, "product", "products")},{" "}
            {totalPricingVariations ?? 0} pricing {pluralize(totalPricingVariations ?? 0, "variation", "variations")} on
            this page.
          </Typography>
        )}
        {!isFetching && isSecondaryTextDisplay && (
          <Typography variant='body2' className='text-gray-600'>
            {totalSelected} of {totalPricingVariations} selected on this page
          </Typography>
        )}
        {modifiedTiersCount > 0 && (
          <Typography variant='body2' className='text-gray-600'>
            {modifiedTiersCount} manually modified {pluralize(modifiedTiersCount, "tier", "tiers")}
          </Typography>
        )}
      </Box>
      <Box className='flex flex-col justify-end gap-3 self-start'>
        <Box className='flex gap-3 whitespace-nowrap'>
          {isResolved && isSellingPrice && !isQuoteSource && (
            <Button
              variant='error'
              disabled={isButtonDisabled || !priceChangeSubmission?.isRevertible}
              startIcon={<SyncOutlined />}
              onClick={handleRevert}
            >
              Revert Price Change
            </Button>
          )}
          {!isResolved && (
            <>
              {!isQuoteSource && (
                <Button
                  appearance='outlined'
                  onClick={onSaveAndExit}
                  disabled={isButtonDisabled || !hasAnyChanges || hasInvalidTiers}
                >
                  Save Changes & Exit
                </Button>
              )}
              <Button
                appearance='outlined'
                variant='error'
                disabled={isButtonDisabled}
                startIcon={<CloseOutlined />}
                onClick={() => setIsDialogOpen(PricingDialogs.DenyPriceChangeConfirmationDialog)}
              >
                {denyLabel}
              </Button>
              <Button
                disabled={isButtonDisabled || hasInvalidTiers}
                appearance='contained'
                startIcon={<CheckOutlined />}
                onClick={onApprove}
              >
                {approveLabel}
              </Button>
            </>
          )}
        </Box>
        <Box className='flex justify-end'>
          {hasInvalidTiers && !isResolved && (
            <Typography variant='body2' className='text-red-600'>
              {totalInvalidCount} required {pluralize(totalInvalidCount, "cell needs", "cells need")} attention before
              saving or approving.
            </Typography>
          )}
        </Box>
      </Box>
      {isDialogOpen === PricingDialogs.DenyPriceChangeConfirmationDialog && (
        <CancelDialog
          title='Are you sure you want to deny this price change?'
          message='This action cannot be undone.'
          confirmLabel='Deny Price Change'
          closeLabel='Go Back'
          onClose={() => setIsDialogOpen(null)}
          onConfirm={handleDeny}
        />
      )}
    </Box>
  )
}

export default ReviewSubmissionHeader
